import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/contexts/LanguageContext';
import SEOHead from '@/components/common/SEOHead';
import { LoadingPage } from '@/components/common/Loading';
import { ChevronRight, Calendar, MapPin, Shield, Swords, Sparkles, Trophy, Flag } from 'lucide-react';
import { renderAIContent, AI_CONTENT_CLASSES } from '@/lib/aiContent';
import WCInternalLinks from '@/components/worldcup/WCInternalLinks';
import { translateCountryName } from '@/lib/wcTranslations';
import WCSubmenu from '@/components/worldcup/WCSubmenu';
import { sanitizeHtml } from '@/lib/sanitize';

const API_BASE = '';

function _teamSlug(name) {
  const map = {
    'Türkiye': 'turkiye', 'Brazil': 'brazil', 'France': 'france', 'Argentina': 'argentina',
    'England': 'england', 'Germany': 'germany', 'Spain': 'spain', 'Portugal': 'portugal',
    'Netherlands': 'netherlands', 'Croatia': 'croatia', 'Belgium': 'belgium', 'USA': 'usa',
    'Mexico': 'mexico', 'Canada': 'canada', 'Japan': 'japan', 'South Korea': 'south-korea',
    'Australia': 'australia', 'Morocco': 'morocco', 'Senegal': 'senegal', 'Uruguay': 'uruguay',
    'Colombia': 'colombia', 'Ecuador': 'ecuador', 'Paraguay': 'paraguay',
    'Switzerland': 'switzerland', 'Austria': 'austria', 'Sweden': 'sweden', 'Norway': 'norway',
    'Scotland': 'scotland', 'Iran': 'iran', 'Saudi Arabia': 'saudi-arabia', 'Qatar': 'qatar',
    'Egypt': 'egypt', 'Tunisia': 'tunisia', 'Algeria': 'algeria', 'Ivory Coast': 'ivory-coast',
    'Ghana': 'ghana', 'Congo DR': 'congo-dr', 'South Africa': 'south-africa',
    'Cape Verde Islands': 'cape-verde', 'New Zealand': 'new-zealand', 'Panama': 'panama',
    'Haiti': 'haiti', 'Bosnia & Herzegovina': 'bosnia', 'Czech Republic': 'czech-republic',
    'Jordan': 'jordan', 'Iraq': 'iraq', 'Uzbekistan': 'uzbekistan', 'Curaçao': 'curacao',
  };
  return map[name] || name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
}

export default function WorldCupTurkey() {
  const { t } = useTranslation();
  const { currentLang } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const wcBase = currentLang === 'tr' ? `/${currentLang}/dunya-kupasi-2026` : `/${currentLang}/world-cup-2026`;

  useEffect(() => {
    fetch(`${API_BASE}/api/worldcup/turkey?lang=${currentLang}`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [currentLang]);

  if (loading) return <LoadingPage />;
  if (!data?.team) return null;

  const { team, group_standings, wc_matches, all_group_matches, qualification_matches, friendly_matches, opponents, seo, ai_content } = data;

  return (
    <div data-testid="worldcup-turkey">
      <WCSubmenu />
      <SEOHead title={seo?.title} description={seo?.description} canonical={seo?.canonical} hreflang={seo?.hreflang} jsonLd={seo?.jsonld} />

      {/* Hero */}
      <div className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #450a0a 0%, #7f1d1d 30%, #dc2626 60%, #450a0a 100%)' }}>
        <div className="absolute top-0 right-0 w-60 h-60 opacity-10">
          <img src={team.logo} alt="" className="w-full h-full object-contain" />
        </div>
        <div className="relative z-10 container mx-auto px-4 max-w-7xl py-10 md:py-16">
          <div className="flex items-center gap-2 text-sm text-white/50 mb-4">
            <Link to={`/${currentLang}`} className="hover:text-white/80">{t('nav.home')}</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to={wcBase} className="hover:text-white/80">Dünya Kupası 2026</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-white">Türkiye</span>
          </div>
          <div className="flex items-center gap-5">
            <img src={team.logo} alt="Türkiye" className="w-20 h-20 md:w-24 md:h-24 drop-shadow-lg" />
            <div>
              <h1 className="text-4xl md:text-5xl font-black text-white" style={{ fontFamily: 'var(--font-heading)' }}>TÜRKİYE</h1>
              <p className="text-lg text-white/70 mt-1">Grup D - FIFA Dünya Kupası 2026</p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 max-w-7xl py-8 space-y-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">

            {/* Group D Standings */}
            <section className="bg-slate-900/60 border border-red-500/10 rounded-xl overflow-hidden">
              <div className="p-4 border-b border-slate-800/50 flex items-center gap-2">
                <Trophy className="w-4 h-4 text-amber-400" />
                <h2 className="text-sm font-bold text-white uppercase tracking-wider">Grup D - Puan Durumu</h2>
              </div>
              <table className="w-full text-sm">
                <thead><tr className="text-xs text-slate-500 border-b border-slate-800/30">
                  <th className="text-left px-4 py-2 w-8">#</th><th className="text-left px-4 py-2">Takım</th>
                  <th className="text-center px-2 py-2">O</th><th className="text-center px-2 py-2">G</th>
                  <th className="text-center px-2 py-2">B</th><th className="text-center px-2 py-2">M</th>
                  <th className="text-center px-2 py-2 font-bold">P</th>
                </tr></thead>
                <tbody>
                  {group_standings.map((s, i) => (
                    <tr key={i} className={`border-b border-slate-800/20 ${s.team_name === 'Türkiye' ? 'bg-red-500/10' : ''}`}>
                      <td className="px-4 py-3 text-slate-500">{s.rank || i+1}</td>
                      <td className="px-4 py-3"><Link to={`${wcBase}/takim/${_teamSlug(s.team_name)}`} className="flex items-center gap-2 hover:text-amber-300 transition-colors"><img src={s.team_logo} alt="" className="w-5 h-5" /><span className={s.team_name === 'Türkiye' ? 'text-red-400 font-semibold' : 'text-slate-300'}>{s.team_name}</span></Link></td>
                      <td className="text-center px-2 py-3 text-slate-400">{s.played}</td>
                      <td className="text-center px-2 py-3 text-emerald-400">{s.won}</td>
                      <td className="text-center px-2 py-3 text-slate-400">{s.drawn}</td>
                      <td className="text-center px-2 py-3 text-rose-400">{s.lost}</td>
                      <td className="text-center px-2 py-3 font-bold text-white">{s.points}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>

            {/* WC Matches */}
            <section className="bg-slate-900/60 border border-white/5 rounded-xl overflow-hidden">
              <div className="p-4 border-b border-slate-800/50 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-amber-400" />
                <h2 className="text-sm font-bold text-white uppercase tracking-wider">Dünya Kupası Maçları</h2>
              </div>
              <div className="divide-y divide-slate-800/30">
                {all_group_matches.map(m => {
                  const date = m.date ? new Date(m.date) : null;
                  const isTR = m.home_name === 'Türkiye' || m.away_name === 'Türkiye';
                  return (
                    <div key={m.id} className={`flex items-center gap-3 px-4 py-3 ${isTR ? 'bg-red-500/5' : ''}`}>
                      <div className="w-14 shrink-0 text-center">
                        <p className="text-xs text-slate-400">{date?.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })}</p>
                        <p className="text-[10px] text-slate-500">{date?.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</p>
                      </div>
                      <img src={m.home_logo} alt="" className="w-5 h-5" />
                      <span className={`text-sm flex-1 ${m.home_name === 'Türkiye' ? 'text-red-400 font-semibold' : 'text-slate-300'}`}>{m.home_name}</span>
                      <span className="text-slate-600 text-xs">vs</span>
                      <span className={`text-sm flex-1 text-right ${m.away_name === 'Türkiye' ? 'text-red-400 font-semibold' : 'text-slate-300'}`}>{m.away_name}</span>
                      <img src={m.away_logo} alt="" className="w-5 h-5" />
                      <span className="text-[10px] text-slate-500 w-28 text-right shrink-0">{m.venue_name}</span>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Qualification Journey */}
            {qualification_matches.length > 0 && (
              <section className="bg-slate-900/60 border border-white/5 rounded-xl overflow-hidden">
                <div className="p-4 border-b border-slate-800/50 flex items-center gap-2">
                  <Flag className="w-4 h-4 text-emerald-400" />
                  <h2 className="text-sm font-bold text-white uppercase tracking-wider">Eleme Yolculuğu</h2>
                </div>
                <div className="divide-y divide-slate-800/30">
                  {qualification_matches.map((m, i) => {
                    const date = m.date ? new Date(m.date) : null;
                    const isTRHome = m.home_name === 'Türkiye';
                    const won = (isTRHome && m.score_home > m.score_away) || (!isTRHome && m.score_away > m.score_home);
                    const drew = m.score_home === m.score_away;
                    return (
                      <div key={i} className="flex items-center gap-3 px-4 py-2.5">
                        <div className="w-14 shrink-0 text-center">
                          <p className="text-[10px] text-slate-500">{date?.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: '2-digit' })}</p>
                        </div>
                        <img src={m.home_logo} alt="" className="w-4 h-4" />
                        <span className={`text-xs flex-1 ${m.home_name === 'Türkiye' ? 'text-red-400 font-medium' : 'text-slate-400'}`}>{m.home_name}</span>
                        <span className={`text-sm font-bold tabular-nums ${won ? 'text-emerald-400' : drew ? 'text-amber-400' : 'text-rose-400'}`}>
                          {m.score_home ?? '-'} - {m.score_away ?? '-'}
                        </span>
                        <span className={`text-xs flex-1 text-right ${m.away_name === 'Türkiye' ? 'text-red-400 font-medium' : 'text-slate-400'}`}>{m.away_name}</span>
                        <img src={m.away_logo} alt="" className="w-4 h-4" />
                        <span className="text-[9px] text-slate-600 w-16 text-right">{m.round.replace('Group Stage - ', t('wc.groupStagePrefix'))}</span>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Friendly Matches */}
            {friendly_matches.length > 0 && (
              <section className="bg-slate-900/60 border border-white/5 rounded-xl overflow-hidden">
                <div className="p-4 border-b border-slate-800/50 flex items-center gap-2">
                  <Shield className="w-4 h-4 text-cyan-400" />
                  <h2 className="text-sm font-bold text-white uppercase tracking-wider">Hazırlık Maçları</h2>
                </div>
                <div className="divide-y divide-slate-800/30">
                  {friendly_matches.map((m, i) => {
                    const date = m.date ? new Date(m.date) : null;
                    return (
                      <div key={i} className="flex items-center gap-3 px-4 py-2.5">
                        <span className="text-[10px] text-slate-500 w-14 text-center shrink-0">{date?.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })}</span>
                        <img src={m.home_logo} alt="" className="w-4 h-4" />
                        <span className={`text-xs flex-1 ${m.home_name === 'Türkiye' ? 'text-red-400' : 'text-slate-400'}`}>{m.home_name}</span>
                        <span className="text-sm font-bold text-slate-300 tabular-nums">{m.score_home ?? '-'} - {m.score_away ?? '-'}</span>
                        <span className={`text-xs flex-1 text-right ${m.away_name === 'Türkiye' ? 'text-red-400' : 'text-slate-400'}`}>{m.away_name}</span>
                        <img src={m.away_logo} alt="" className="w-4 h-4" />
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Full AI Analysis */}
            {ai_content?.turkey_analysis && (
              <section className="bg-gradient-to-br from-slate-800 to-slate-900 border border-amber-500/20 rounded-xl p-6 shadow-[0_0_15px_rgba(251,191,36,0.05)]">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="w-5 h-5 text-amber-400" />
                  <h2 className="text-sm font-bold text-white uppercase tracking-wider">Türkiye Dünya Kupası 2026 Analizi</h2>
                </div>
                <div className={AI_CONTENT_CLASSES}
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(renderAIContent(ai_content.turkey_analysis)) }} />
              </section>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Opponents */}
            <div className="bg-slate-900/60 border border-red-500/10 rounded-xl p-4">
              <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-3"><Swords className="w-4 h-4 inline mr-2 text-red-400" />Rakipler</h3>
              {opponents.map((o, i) => (
                <div key={i} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-800/30 mb-1">
                  <img src={o.logo} alt="" className="w-8 h-8" />
                  <span className="text-sm font-medium text-slate-300">{o.name}</span>
                </div>
              ))}
            </div>

            {/* AI Analysis */}
            <div className="bg-gradient-to-br from-red-950/30 to-slate-900 border border-red-500/20 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-amber-400" />
                <h3 className="text-sm font-bold text-white">Türkiye Analizi</h3>
              </div>
              {ai_content?.turkey_analysis ? (
                <div className="text-xs text-slate-300 leading-relaxed max-h-[300px] overflow-y-auto"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(renderAIContent(ai_content.turkey_analysis).slice(0, 3000)) }} />
              ) : (
                <p className="text-xs text-slate-400">Türkiye analizi yakında eklenecek.</p>
              )}
            </div>

            <Link to={`${wcBase}/grup/d`} className="block bg-slate-800/50 border border-white/5 rounded-xl p-3 text-center text-sm text-amber-400 hover:text-amber-300 hover:bg-slate-800 transition-colors">
              Grup D Detay Sayfası
            </Link>
          </div>
        </div>

        <WCInternalLinks currentPage="turkey" lang={currentLang} />
      </div>
    </div>
  );
}
